<p-confirmDialog [appendTo]="'body'" />

<!-- Rendered on body: embedded in the FA client page this sits inside the
     content splitter, which isolates its stacking context, so a dialog left in
     place is painted over by the shell chrome above it. -->
<p-dialog
  [visible]="visible()"
  (visibleChange)="visible.set($event)"
  [modal]="true"
  [draggable]="false"
  [dismissableMask]="true"
  [showHeader]="false"
  [appendTo]="'body'"
  [style]="{ width: '44rem', maxWidth: '95vw' }"
  styleClass="bn-detail-dialog"
>
  <header class="bn-detail__header">
    <div class="bn-detail__header-top">
      @if (clientName() || activity().client_guid) {
        <span
          class="bn-detail__client"
          [pTooltip]="activity().client_guid ?? ''"
          tooltipPosition="bottom"
        >
          <i class="pi pi-user"></i>
          {{ clientName() ?? activity().client_guid }}
        </span>
      } @else {
        <span class="bn-detail__client bn-detail__client--unknown"
          >No client on record</span
        >
      }

      <button
        type="button"
        class="bn-detail__close"
        aria-label="Close"
        (click)="close()"
      >
        <i class="pi pi-times"></i>
      </button>
    </div>

    <h2 class="bn-detail__title" data-testid="detail-title">
      {{ activity().title }}
    </h2>
    <p class="bn-detail__description">{{ activity().description }}</p>
    <p class="bn-detail__proposed">Proposed {{ proposedAgo() }}</p>
  </header>

  <div class="bn-detail__body">
    <section>
      <h3 class="bn-detail__section-label">
        Proposed actions
        <span class="bn-detail__section-count">{{ actions().length }}</span>
        @if (breakdown()) {
          <span class="bn-detail__section-note">{{ breakdown() }}</span>
        }
      </h3>

      @if (actions().length === 0) {
        <p class="bn-detail__no-actions">
          The router agent read this window and proposed nothing actionable.
        </p>
      }

      @for (action of actions(); track $index) {
        <article
          class="bn-action"
          [attr.data-testid]="'detail-action-' + $index"
        >
          <div class="bn-action__head">
            <span class="bn-action__index">{{ $index + 1 }}</span>
            <span
              class="bn-action__type"
              [ngClass]="actionTypeMetaFor(action.action_type).chipClass"
              [pTooltip]="actionTypeMetaFor(action.action_type).description"
              tooltipPosition="bottom"
            >
              {{ actionTypeMetaFor(action.action_type).label }}
            </span>
            <h4 class="bn-action__title">{{ action.title }}</h4>
            <span
              class="bn-action__disposition"
              [ngClass]="dispositionMeta[action.disposition].chipClass"
            >
              {{ dispositionMeta[action.disposition].label }}
            </span>
          </div>

          <p class="bn-action__fold-label">Rationale</p>
          <p
            class="bn-action__rationale"
            [attr.data-testid]="'detail-rationale-' + $index"
          >
            {{ action.rationale }}
          </p>

          <button
            type="button"
            class="bn-action__fold-toggle"
            [attr.aria-expanded]="isInstructionOpen($index)"
            [attr.data-testid]="'detail-instruction-toggle-' + $index"
            (click)="toggleInstruction($index)"
          >
            <i
              class="pi"
              [class.pi-angle-right]="!isInstructionOpen($index)"
              [class.pi-angle-down]="isInstructionOpen($index)"
            ></i>
            Instruction
          </button>
          @if (isInstructionOpen($index)) {
            <p
              class="bn-action__instruction"
              [attr.data-testid]="'detail-instruction-' + $index"
            >
              {{ action.instruction }}
            </p>
          }
        </article>
      }
    </section>

    <section class="bn-trigger">
      <div class="bn-trigger__head">
        <span class="bn-detail__field-label">
          <i class="pi pi-comment"></i>
          What triggered this
        </span>
        <span class="bn-trigger__source">{{ triggerSource() }}</span>
      </div>

      @if (triggerMessages().length) {
        <div class="bn-trigger__messages">
          @for (message of triggerMessages(); track $index) {
            <blockquote
              class="bn-trigger__message"
              [class.bn-trigger__message--trigger]="message.isTrigger"
              [attr.data-testid]="'detail-trigger-message-' + $index"
            >
              {{ message.body }}
            </blockquote>
          }
        </div>
      }

      @if (activity().conversation_link; as link) {
        <a
          class="bn-trigger__link"
          [href]="link"
          target="_blank"
          rel="noopener noreferrer"
        >
          Open full conversation
          <i class="pi pi-external-link"></i>
        </a>
      }
    </section>

    @if (notes().length > 0) {
      <section class="bn-notes" data-testid="detail-notes">
        <p class="bn-detail__field-label">
          Past notes
          <span class="bn-detail__section-count">{{ notes().length }}</span>
          <span class="bn-detail__field-hint">oldest first</span>
        </p>
        <ol>
          @for (note of notes(); track $index) {
            <li>
              <p class="bn-notes__meta">
                <span class="bn-notes__actor">{{ note.actor }}</span>
                <span>{{ note.absolute }} &middot; {{ note.relative }}</span>
              </p>
              <p class="bn-notes__text">{{ note.note }}</p>
            </li>
          }
        </ol>
      </section>
    }

    <section class="bn-debug" #debugSection>
      <button
        type="button"
        class="bn-debug__toggle"
        [attr.aria-expanded]="debugOpen()"
        data-testid="detail-debug-toggle"
        (click)="toggleDebug()"
      >
        Debug info
        <i
          class="pi"
          [class.pi-angle-down]="!debugOpen()"
          [class.pi-angle-up]="debugOpen()"
        ></i>
      </button>

      @if (debugOpen()) {
        <div class="bn-debug__panel">
          <dl class="bn-debug__rows">
            @for (row of debugRows(); track row.label) {
              <div class="bn-debug__row">
                <dt>{{ row.label }}</dt>
                <dd>
                  @if (row.value) {
                    <span class="bn-debug__value">{{ row.value }}</span>
                    <button
                      type="button"
                      class="bn-debug__copy"
                      [attr.aria-label]="'Copy ' + row.label"
                      pTooltip="Copy"
                      tooltipPosition="left"
                      (click)="copy(row)"
                    >
                      <i class="pi pi-copy"></i>
                    </button>
                    @if (row.href) {
                      <a
                        [href]="row.href"
                        target="_blank"
                        rel="noopener noreferrer"
                        [attr.aria-label]="'Open ' + row.label"
                      >
                        <i class="pi pi-external-link"></i>
                      </a>
                    }
                  } @else {
                    <span class="bn-debug__empty">not set</span>
                  }
                </dd>
              </div>
            }
          </dl>
        </div>
      }
    </section>
  </div>

  <footer class="bn-detail__footer">
    @if (settled(); as reason) {
      <p class="bn-detail__settled" data-testid="detail-settled">
        <i class="pi pi-lock"></i>
        {{ reason }}
      </p>
    } @else {
      <label class="bn-detail__field-label" for="bn-note">
        Note
        @if (rebuildEnabled()) {
          <span class="bn-detail__field-hint"
            >optional to confirm &middot; required to rebuild</span
          >
        }
      </label>

      <textarea
        pTextarea
        id="bn-note"
        rows="4"
        [autoResize]="true"
        class="bn-detail__note"
        data-testid="detail-note"
        [placeholder]="
          rebuildEnabled()
            ? 'Optional for Confirm. Required for Rebuild Activity — this text is the feedback the router agent re-runs with.'
            : 'Optional for Confirm.'
        "
        [value]="noteDraft()"
        (input)="noteDraft.set($any($event.target).value)"
      ></textarea>

      <div class="bn-detail__buttons">
        @if (rebuildEnabled()) {
          <p-button
            icon="pi pi-refresh"
            label="Rebuild Activity"
            size="small"
            severity="secondary"
            [outlined]="true"
            [loading]="busy() === 'retry'"
            data-testid="detail-retry"
            (onClick)="requestAction('retry')"
          />
        }
        <div class="bn-detail__buttons-end">
          <p-button
            label="Confirm"
            size="small"
            [loading]="busy() === 'confirm'"
            [disabled]="actions().length === 0"
            data-testid="detail-confirm"
            (onClick)="requestAction('confirm')"
          />
          <p-button
            label="Deny"
            size="small"
            severity="danger"
            [outlined]="true"
            [loading]="busy() === 'deny'"
            data-testid="detail-deny"
            (onClick)="requestAction('deny')"
          />
        </div>
      </div>
    }
  </footer>
</p-dialog>
